import type { Header } from '@tanstack/react-table';
import * as React from 'react';

import BaseHeaderCell from './BaseHeaderCell';
import HeaderGridCell from './HeaderGridCell';
import type { HeaderLayoutEntry } from './HeaderGroups';

interface OwnProps<T> {
	/** Bucket: key = columnStart, value = headers that start at that column */
	boundaryIndex: Map<number, Array<{ headerId: string; rowStart: number; rowEnd: number; }>>;
	columnStart: number;
	header: Header<T, unknown>;
	headerLeafIds: string[];
	headerLayout: Map<string, HeaderLayoutEntry>;
	leafIdIndex: Map<string, number>;
	leafIds: string[];
	onColumnDrag: (header: string) => (e: React.MouseEvent<Element, MouseEvent>) => void;
	setHeaderReference: (headerId: string) => (_ref: HTMLDivElement) => void;
	fixedLeafColumnStyles: Record<string, React.CSSProperties>;
	totalLeafCount: number;
	maxDepth: number;
}

type Props<T> = OwnProps<T>;

function HeaderGroupItem<T>(props: Props<T>) {
	const {
		boundaryIndex,
		columnStart,
		header,
		headerLeafIds,
		headerLayout,
		leafIdIndex,
		leafIds,
		onColumnDrag,
		setHeaderReference,
		fixedLeafColumnStyles,
		totalLeafCount,
		maxDepth,
	} = props;

	const layout = headerLayout.get(header.id);
	const columnEnd = layout?.columnEnd ?? columnStart + headerLeafIds.length;

	const isFullyFixedHeader = React.useMemo(() => {
		return headerLeafIds.length > 0 && headerLeafIds.every((leafId) => !!fixedLeafColumnStyles[leafId]);
	}, [fixedLeafColumnStyles, headerLeafIds]);

	const fixedStyle = isFullyFixedHeader
		? {
			...fixedLeafColumnStyles[headerLeafIds[0]],
			zIndex: 10,
		}
		: undefined;

	const needsFixedRightBorder = React.useMemo(() => {
		if (!isFullyFixedHeader) {
			return false;
		}

		const lastLeafId = headerLeafIds[headerLeafIds.length - 1];
		if (!lastLeafId) {
			return false;
		}

		// Use global leaf index map to find the next global leaf
		const globalLeafIndex = leafIdIndex.get(lastLeafId);
		if (globalLeafIndex === undefined) {
			return false;
		}

		// O(1): direct array access, identical to original leafIds[globalLeafIndex + 1]
		const nextLeafId = leafIds[globalLeafIndex + 1];

		if (!nextLeafId) {
			return false;
		}

		return !fixedLeafColumnStyles[nextLeafId];
	}, [fixedLeafColumnStyles, headerLeafIds, isFullyFixedHeader, leafIdIndex, leafIds]);

	const layoutEntry = layout ?? { columnStart, columnEnd, rowStart: 0, rowEnd: 0 };
	const hasSubHeaders = header.subHeaders.length > 0;
	const rowStart = layoutEntry.rowStart;
	const rowEnd = layoutEntry.rowEnd;

	const isLeaf = !hasSubHeaders;

	const shouldSpanFullHeaderHeight = React.useMemo(() => {
		return isFullyFixedHeader
			&& isLeaf
			&& header.column.depth === 0;
	}, [header.column.depth, isFullyFixedHeader, isLeaf]);

	const resolvedRowStart = shouldSpanFullHeaderHeight
		? 1
		: rowStart;

	const resolvedRowEnd = shouldSpanFullHeaderHeight
		? maxDepth + 1
		: rowEnd;

	const isLastCol = React.useMemo(() => {
		return columnEnd === totalLeafCount + 1;
	}, [columnEnd, totalLeafCount]);

	const needsTopBorder = React.useMemo(() => {
		return resolvedRowStart > 1;
	}, [resolvedRowStart]);

	const hasRightNeighborCoveringSameVerticalRange = React.useMemo(() => {
		const candidates = boundaryIndex.get(columnEnd);
		if (!candidates) {
			return false;
		}

		return candidates.some((other) => {
			if (other.headerId === header.id) {
				return false;
			}
			return other.rowStart <= rowStart && other.rowEnd >= rowEnd;
		});
	}, [boundaryIndex, columnEnd, header.id, rowStart, rowEnd]);

	const needsRightBorder = React.useMemo(() => {
		return !isLeaf &&
			!isLastCol &&
			!hasRightNeighborCoveringSameVerticalRange;
	}, [
		hasRightNeighborCoveringSameVerticalRange,
		isLastCol,
		isLeaf,
	]);

	if (!headerLeafIds.length) {
		return null;
	}

	return (
		<HeaderGridCell
			colSpan={header.colSpan}
			columnStart={columnStart}
			isFirstCol={columnStart === 1}
			isLastCol={isLastCol}
			isLeaf={isLeaf}
			key={header.id}
			needsFixedRightBorder={needsFixedRightBorder}
			needsRightBorder={needsRightBorder}
			needsTopBorder={needsTopBorder}
			rowEnd={resolvedRowEnd}
			rowStart={resolvedRowStart}
			style={fixedStyle}
		>
			<BaseHeaderCell<T>
				header={header}
				isLeafHeader={!hasSubHeaders}
				onColumnDrag={onColumnDrag}
				setHeaderReference={setHeaderReference}
			/>
		</HeaderGridCell>
	);
}

export default React.memo(HeaderGroupItem) as typeof HeaderGroupItem;
